import ScrollableSection from '@components/v2/layout/ScrollableSection/ScrollableSection';
import { useWorkspacePadding } from '@components/v2/layout/hooks/useWorkspacePadding';
import { Box, Stack } from '@mui/material';
import { Group, Panel } from 'react-resizable-panels';
import { useObjectDetailsPanelLayout } from '../hooks/useObjectDetailsPanelLayout';
import { useObjectDetailsViewport } from '../hooks/useObjectDetailsViewport';
import { OBJECT_CONTENT_PANEL_ID } from '../objectScreen.constants';
import { ObjectLayoutProps } from '../objectScreen.types';
import { ObjectDetailsSlot } from './ObjectDetailsSlot/ObjectDetailsSlot';

export const ObjectStickyLayout = ({
  objectType,
  header,
  content,
  afterHeaderSlots,
  afterToolbarSlots,
  tabsRow,
  filterBar,
  afterFilterBarSlots,
  details,
  scrollParent,
  stickyToolbar,
  setScrollParent,
  setStickyToolbar,
  setStickyFilterBar,
}: ObjectLayoutProps) => {
  const { padding: workspacePadding } = useWorkspacePadding();
  const { toolbarHeight, detailsHeight } = useObjectDetailsViewport({
    scrollParent,
    stickyToolbar,
  });
  const { defaultLayout, onLayoutChanged } = useObjectDetailsPanelLayout(objectType);

  return (
    <Stack sx={{ height: '100%', minHeight: 0, bgcolor: 'transparent' }}>
      {header}
      <Stack
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          mx: -workspacePadding,
        }}
      >
        <ScrollableSection ref={setScrollParent} bleed={false} sx={{ mb: 1 }}>
          {afterHeaderSlots}
          <Box
            ref={setStickyToolbar}
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 4,
              px: workspacePadding,
              bgcolor: 'background.paper',
            }}
          >
            {tabsRow}
            {afterToolbarSlots}
          </Box>
          <Stack
            direction="row"
            sx={{ minHeight: detailsHeight, overflowX: 'clip', pl: workspacePadding }}
          >
            <Group
              orientation="horizontal"
              defaultLayout={defaultLayout}
              style={{ height: 'auto', overflow: 'visible' }}
              onLayoutChanged={onLayoutChanged}
            >
              <Panel
                id={OBJECT_CONTENT_PANEL_ID}
                style={{ display: 'flex', flexDirection: 'column', overflow: 'visible' }}
              >
                <Box
                  ref={setStickyFilterBar}
                  sx={{
                    position: 'sticky',
                    top: toolbarHeight,
                    zIndex: 3,
                    pb: 1.5,
                    ml: -workspacePadding,
                    pl: workspacePadding,
                    bgcolor: 'background.paper',
                  }}
                >
                  {filterBar}
                  {afterFilterBarSlots}
                </Box>
                {content}
              </Panel>
              {details.panel && (
                <ObjectDetailsSlot
                  panel={details.panel}
                  isOpen={details.isOpen}
                  isInlineOnTablet={details.isInlineOnTablet}
                  isStickyLayout
                  stickyTop={toolbarHeight}
                  height={detailsHeight}
                  onClose={details.onClose}
                  onExpansionStepsChange={details.onExpansionStepsChange}
                />
              )}
            </Group>
          </Stack>
        </ScrollableSection>
      </Stack>
    </Stack>
  );
};
